import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, Filter, X } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import PromoCard from "@/components/site/PromoCard";
import { CLASSES, BRANCHES } from "@/lib/data";
import { PROMOS } from "@/lib/promos";
import { buildClassEnquiry, buildAltClassEnquiry, buildClassEmailEnquiry, CLASS_COORDINATORS, ALT_CLASS_COORDINATORS, BRANCH_EMAILS } from "@/lib/whatsapp";

const LEVELS = ["All level", "Beginner", "Intermediate", "Advanced", "Professional"];
const STYLES = ["Hip Hop", "Contemporary", "Indian Cinematic", "Folk", "Salsa", "Bollywood", "Bhangra", "Classical", "Gymnastics", "Dance Fitness"];
// Concrete styles have dedicated classes in the catalog — filter strictly by exact match.
// Other styles only appear inside multi-style "All Style" classes — fall back to those.
const CONCRETE_STYLES = ["Bollywood", "Bhangra", "Classical", "Gymnastics", "Dance Fitness"];
const AGES = ["Toddlers", "Kids", "Juniors", "Teens", "Adults", "All ages"];
const MODES = ["Online", "Offline"];
const MODE_LABELS = { Offline: "In-Person Studio", Online: "Online", All: "All" };

export default function Classes() {
  const [branch, setBranch] = useState("All");
  const [level, setLevel] = useState("All");
  const [style, setStyle] = useState("All");
  const [age, setAge] = useState("All");
  const [mode, setMode] = useState("All");

  const filtered = useMemo(() => {
    return CLASSES.filter((c) => {
      if (!(branch === "All" || c.branch === branch || c.branch === "All")) return false;
      if (!(level === "All" || c.level === level)) return false;
      if (!(age === "All" || c.ageBand === age)) return false;
      if (!(mode === "All" || c.mode === mode)) return false;
      if (style === "All") return true;
      // Concrete styles: strict match. Other styles: match the multi-style "All Style" classes.
      if (CONCRETE_STYLES.includes(style)) return c.style === style;
      return c.style === "All Style";
    });
  }, [branch, level, style, age, mode]);

  const Pill = ({ active, onClick, children, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 h-9 text-xs uppercase tracking-wider font-semibold border transition-colors ${
        active ? "bg-[#ff4500] border-[#ff4500] text-white" : "border-white/15 text-white/70 hover:border-white/40"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div data-testid="classes-page">
      <section className="relative h-[44vh] min-h-[360px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1690267717892-b88473bc771c?w=2000" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Classes & Programs</p>
          <h1 className="font-display text-5xl lg:text-7xl text-white leading-[0.95]">
            Find your <span className="phoenix-text">class.</span>
          </h1>
        </div>
      </section>

      <section className="py-12 max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Filters */}
        <div className="bg-[#0a0a0a] border border-white/10 p-6 lg:p-8 space-y-5" data-testid="classes-filters">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/50">
            <Filter className="w-3.5 h-3.5" /> Filter
          </div>
          {[
            { label: "Branch", value: branch, set: setBranch, opts: ["All", ...BRANCHES.map((b) => b.name)], k: "branch" },
            { label: "Level", value: level, set: setLevel, opts: ["All", ...LEVELS], k: "level" },
            { label: "Style", value: style, set: setStyle, opts: ["All", ...STYLES], k: "style" },
            { label: "Age", value: age, set: setAge, opts: ["All", ...AGES], k: "age" },
            { label: "Mode", value: mode, set: setMode, opts: ["All", ...MODES], k: "mode", labels: MODE_LABELS },
          ].map((g) => (
            <div key={g.k} className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 w-16">{g.label}</span>
              {g.opts.map((o) => (
                <Pill key={`${g.k}-${o}`} active={g.value === o} onClick={() => g.set(o)} testid={`filter-${g.k}-${o.toLowerCase().replace(/\s|\//g, "-")}`}>
                  {g.labels ? (g.labels[o] || o) : o}
                </Pill>
              ))}
            </div>
          ))}
          <div className="flex justify-between items-center pt-3 border-t border-white/10">
            <p className="text-sm text-white/50">{filtered.length} classes found</p>
            <button
              data-testid="reset-filters"
              onClick={() => { setBranch("All"); setLevel("All"); setStyle("All"); setAge("All"); setMode("All"); }}
              className="text-xs uppercase tracking-wider text-white/60 hover:text-[#ff4500] inline-flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Reset
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => {
            const isMultiBranch = c.branch === "All";
            const flags = { India: "🇮🇳", USA: "🇺🇸", Canada: "🇨🇦" };
            return (
              <div key={c.id} data-testid={`class-card-${c.id}`} className="bg-[#0a0a0a] border border-white/10 p-7 card-hover flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${c.mode === "Online" ? "bg-[#ffb700]/15 text-[#ffb700] border border-[#ffb700]/30" : "bg-[#ff4500]/15 text-[#ff4500] border border-[#ff4500]/30"}`}>
                    {MODE_LABELS[c.mode] || c.mode}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/50">{c.branch}</span>
                </div>
                <h3 className="font-display text-2xl text-white">{c.title}</h3>
                <p className="text-xs uppercase tracking-wider text-white/40 mt-1">{c.style}</p>
                <div className="mt-5 space-y-2 text-sm text-white/65 flex-1">
                  <p><span className="text-white/40">Level:</span> {c.level}</p>
                  <p><span className="text-white/40">Age:</span> {c.age}</p>
                  <p><span className="text-white/40">Schedule:</span> {c.schedule}</p>
                  <p><span className="text-white/40">Duration:</span> {c.duration}</p>
                  {c.note && (
                    <p className="text-[11px] text-[#ffb700] pt-2 leading-snug">
                      ◆ {c.note}
                    </p>
                  )}
                </div>

                {isMultiBranch ? (
                  <div className="mt-6">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">
                      Choose your branch
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.keys(CLASS_COORDINATORS).map((b) => {
                        const link = buildClassEnquiry(c, b);
                        if (!link) {
                          return (
                            <span
                              key={b}
                              data-testid={`class-cta-${c.id}-${b.toLowerCase()}-disabled`}
                              className="border border-white/10 text-white/30 h-12 inline-flex flex-col items-center justify-center text-[10px] uppercase tracking-wider font-semibold cursor-not-allowed"
                              title="Coordinator number coming soon"
                            >
                              <span>{flags[b]} {b}</span>
                              <span className="text-[8px] text-white/30">Coming soon</span>
                            </span>
                          );
                        }
                        return (
                          <a
                            key={b}
                            href={link}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`class-cta-${c.id}-${b.toLowerCase()}`}
                            className="border border-white/15 text-white h-12 inline-flex items-center justify-center gap-1 text-[11px] uppercase tracking-wider font-semibold hover:bg-[#25D366] hover:border-[#25D366] transition-colors"
                          >
                            <span>{flags[b]}</span>
                            <span>{b}</span>
                          </a>
                        );
                      })}
                    </div>
                    {Object.keys(ALT_CLASS_COORDINATORS).map((b) => {
                      const link = buildAltClassEnquiry(c, b);
                      if (!link) return null;
                      return (
                        <a
                          key={`${c.id}-${b}-alt`}
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`class-cta-${c.id}-${b.toLowerCase()}-alt`}
                          className="mt-2 w-full border border-[#25D366]/40 text-white h-10 inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider font-semibold hover:bg-[#25D366] hover:border-[#25D366] transition-colors"
                        >
                          <MessageCircle className="w-3.5 h-3.5" /> {flags[b]} Alt WhatsApp ({b})
                        </a>
                      );
                    })}
                    {Object.keys(BRANCH_EMAILS).map((b) => {
                      const link = buildClassEmailEnquiry(c, b);
                      if (!link) return null;
                      return (
                        <a
                          key={`${c.id}-${b}-email`}
                          href={link}
                          data-testid={`class-cta-${c.id}-${b.toLowerCase()}-email`}
                          className="mt-2 w-full border border-white/15 text-white h-10 inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider font-semibold hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors"
                        >
                          <Mail className="w-3.5 h-3.5" /> {flags[b]} Email {b} instead
                        </a>
                      );
                    })}
                  </div>
                ) : buildClassEnquiry(c) ? (
                  <div className="mt-6 flex flex-col sm:flex-row gap-2 flex-wrap">
                    <a
                      href={buildClassEnquiry(c)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`class-cta-${c.id}`}
                      className="flex-1 btn-phoenix bg-[#ff4500] text-white h-12 uppercase tracking-wider text-xs font-semibold inline-flex items-center justify-center gap-2 min-w-[180px]"
                    >
                      <MessageCircle className="w-4 h-4" /> Enquire on WhatsApp
                    </a>
                    {buildAltClassEnquiry(c) && (
                      <a
                        href={buildAltClassEnquiry(c)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`class-cta-${c.id}-alt`}
                        className="flex-1 border border-[#25D366]/50 text-white h-12 uppercase tracking-wider text-xs font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#25D366] hover:border-[#25D366] transition-colors min-w-[180px]"
                      >
                        <MessageCircle className="w-4 h-4" /> Alt WhatsApp
                      </a>
                    )}
                    {buildClassEmailEnquiry(c) && (
                      <a
                        href={buildClassEmailEnquiry(c)}
                        data-testid={`class-cta-${c.id}-email`}
                        className="flex-1 border border-white/20 text-white h-12 uppercase tracking-wider text-xs font-semibold inline-flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors min-w-[180px]"
                      >
                        <Mail className="w-4 h-4" /> Enquire via Email
                      </a>
                    )}
                  </div>
                ) : (
                  <span
                    data-testid={`class-cta-${c.id}-disabled`}
                    className="mt-6 w-full border border-white/10 text-white/40 h-12 uppercase tracking-wider text-xs font-semibold inline-flex items-center justify-center gap-2 cursor-not-allowed"
                  >
                    Coordinator coming soon
                  </span>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full p-12 text-center text-white/50 border border-dashed border-white/15">
              No classes match these filters. Try resetting.
            </div>
          )}
        </div>
      </section>

      {/* Talk to Advisor */}
      <section className="py-24 ember-bg border-y border-white/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Personalised Help</p>
            <h2 className="font-display text-4xl lg:text-5xl text-white leading-[1]">
              Talk to a class advisor.
            </h2>
            <p className="mt-5 text-white/65 text-base lg:text-lg">
              Not sure which program fits you? Our advisors will help you pick the right level, branch, and schedule.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <a href="tel:+918248214953" data-testid="advisor-call" className="bg-[#0a0a0a] border border-white/15 p-5 hover:border-[#ff4500] transition-colors flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#ff4500]" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/40">Call us</p>
                  <p className="text-white text-base">+91 82482 14953</p>
                </div>
              </div>
              <span className="text-[#ff4500] text-xs uppercase tracking-wider">Now →</span>
            </a>
            <a href="https://wa.me/918248214953" target="_blank" rel="noreferrer" data-testid="advisor-wa" className="bg-[#0a0a0a] border border-white/15 p-5 hover:border-[#25D366] transition-colors flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/40">WhatsApp</p>
                  <p className="text-white text-base">+91 82482 14953</p>
                </div>
              </div>
              <span className="text-[#25D366] text-xs uppercase tracking-wider">Open →</span>
            </a>
          </div>
        </div>
      </section>

      {/* PDA USA promos */}
      <section
        data-testid="classes-promos"
        className="py-20 lg:py-24 max-w-[1400px] mx-auto px-6 lg:px-10"
      >
        <SectionHeading
          eyebrow="PDA USA · Now enrolling"
          title="Fall 2026 – Spring 2027"
          accent="schedule."
          subtitle="Classes resume Monday, August 3 — In-Person at PDA Studio and Online on Zoom. Tap below to enroll."
          testid="classes-promos-heading"
        />
        <div className="mt-10 grid grid-cols-1 gap-10">
          {PROMOS.map((p) => (
            <PromoCard key={p.id} promo={p} />
          ))}
        </div>
      </section>

      {/* What's included */}
      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow="What's included" title="Every Phoenix" accent="enrollment includes." testid="includes-heading" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          {[
            { t: "Free trial class", d: "Try any class with no commitment before you join." },
            { t: "Structured syllabus", d: "Clear progression from beginner to performance-ready." },
            { t: "Showcase access", d: "Bi-annual student recitals at every Phoenix branch." },
            { t: "Branch transfer", d: "Train at any of our three studios — same membership." },
            { t: "Online + offline", d: "Switch modes anytime as your schedule shifts." },
            { t: "Performance prep", d: "Coaching for competitions, festivals, and auditions." },
            { t: "Faculty mentorship", d: "Direct access to our senior choreographers." },
            { t: "Phoenix community", d: "Crew nights, jams, and member-only intensives." },
          ].map((b, i) => (
            <div key={i} data-testid={`incl-${i}`} className="bg-[#0a0a0a] p-7 hover:bg-[#0f0f0f] transition-colors">
              <p className="font-display text-3xl phoenix-text">0{i + 1}</p>
              <h4 className="font-display text-xl text-white mt-3">{b.t}</h4>
              <p className="text-sm text-white/60 mt-2 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
